// Meal-plan generation client.
//
// Contract:
//   * Calls the trusted Supabase RPC public.generate_meal_plans as
//     the authenticated customer. The browser never performs any
//     meal-plan scoring, eligibility filtering, allergen filtering,
//     or restriction filtering.
//
// Idempotency + freshness (Phase B4 — stale-generation fix):
//   * A client-generated UUID (`generation_request_id`) is persisted
//     ALONGSIDE a deterministic fingerprint of generation-relevant
//     planner inputs in localStorage under
//     `etff.generation_request.v1`.
//   * Retries with UNCHANGED generation inputs reuse the same UUID
//     so Supabase replays the same server-persisted plans (idempotent).
//   * As soon as any generation-relevant input CHANGES, the UUID is
//     rotated on the next Build-my-plans and the cached result is
//     cleared so no stale plan can render.
//   * The legacy key `etff.generation_request_id.v1` is preserved
//     for backward compatibility only when the fingerprint agrees;
//     otherwise it is replaced.
//
// Result caching:
//   * The last successful RPC payload is cached in sessionStorage
//     (`etff.generation_result.v1`) so /plan/results survives a
//     refresh without re-invoking the RPC. The cache is cleared
//     whenever a new UUID is minted.

import { supabase, supabaseConfigured } from './supabase';

const REQ_META_KEY = 'etff.generation_request.v1';       // { id, fingerprint }
const LEGACY_REQ_ID_KEY = 'etff.generation_request_id.v1'; // legacy — id only
const CACHE_KEY = 'etff.generation_result.v1';

function isBrowser() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function makeUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const h = [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// -------------------- Generation fingerprint --------------------
// A deterministic, stable serialization of ONLY the planner fields
// that influence generation output. No timestamps, no user ids, no
// draft revision numbers, no UI-only state.
export function computeGenerationFingerprint(draft) {
  if (!draft || typeof draft !== 'object') return 'empty';
  const b = draft.body || {};
  const stringOrNull = (v) => (typeof v === 'string' && v.length > 0 ? v : null);
  const numOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const sortedArr = (a, other) => {
    const list = Array.isArray(a) ? a.slice() : [];
    list.sort();
    if (other) {
      const t = String(other || '').trim();
      if (t) list.push('__other__:' + t);
    }
    return list;
  };
  const shape = {
    days: numOrNull(draft.days),
    meals_per_day: numOrNull(draft.meals_per_day),
    health_goal: stringOrNull(draft.health_goal),
    sex: stringOrNull(b.sex),
    activity_level: stringOrNull(b.activity_level),
    height_cm: numOrNull(b.normalized_height_cm),
    weight_kg: numOrNull(b.normalized_weight_kg),
    calorie_source: stringOrNull(draft.calorie_source),
    calorie_target: numOrNull(draft.calorie_target),
    macro_preset: stringOrNull(draft.macro_preset),
    protein_g: numOrNull(draft.protein_g),
    carbs_g: numOrNull(draft.carbs_g),
    fat_g: numOrNull(draft.fat_g),
    allergies: sortedArr(draft.allergies, draft.allergies_other_description),
    restrictions: sortedArr(draft.restrictions, draft.restrictions_other_description),
    preferences: sortedArr(draft.preferences, null),
  };
  try {
    return JSON.stringify(shape);
  } catch {
    return 'unserializable:' + Date.now();
  }
}

// -------------------- Request metadata storage ------------------

function readMeta() {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(REQ_META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && /^[0-9a-f-]{36}$/i.test(parsed.id || '') && typeof parsed.fingerprint === 'string') {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  // Legacy-only migration: if we have the old id-only key and no meta,
  // return it as a meta with an empty fingerprint so ANY current
  // fingerprint mismatches and we rotate on the next generation.
  try {
    const legacy = window.localStorage.getItem(LEGACY_REQ_ID_KEY);
    if (legacy && /^[0-9a-f-]{36}$/i.test(legacy)) {
      return { id: legacy, fingerprint: '__legacy__' };
    }
  } catch { /* ignore */ }
  return null;
}

function writeMeta(meta) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(REQ_META_KEY, JSON.stringify(meta));
    // Keep the legacy key aligned so any other code reading it sees
    // the current id.
    window.localStorage.setItem(LEGACY_REQ_ID_KEY, meta.id);
  } catch { /* ignore quota */ }
}

function removeMeta() {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(REQ_META_KEY);
    window.localStorage.removeItem(LEGACY_REQ_ID_KEY);
  } catch { /* ignore */ }
}

// -------------------- Public API --------------------------------

// Preferred path: get (or mint) the generation_request_id for the
// current generation-input fingerprint. If the stored fingerprint
// matches, the same UUID is returned (idempotent retry). Otherwise
// a fresh UUID is minted, meta is rewritten, and the cached result
// is cleared so no stale plan can render.
//
// Returns { id, rotated } where rotated===true when a new UUID was
// minted this call.
export function getOrCreateGenerationRequestIdForFingerprint(fingerprint) {
  const stored = readMeta();
  if (stored && stored.fingerprint === fingerprint) {
    return { id: stored.id, rotated: false };
  }
  const id = makeUuid();
  writeMeta({ id, fingerprint });
  clearCachedGenerationResult();
  return { id, rotated: true };
}

// Legacy accessor — returns the stored id (or mints one with an
// empty/unknown fingerprint). Kept for backward compatibility only.
export function getOrCreateGenerationRequestId() {
  const stored = readMeta();
  if (stored) return stored.id;
  const id = makeUuid();
  writeMeta({ id, fingerprint: '__unknown__' });
  return id;
}

export function peekGenerationRequestId() {
  return readMeta()?.id || null;
}

// Force a fresh generation_request_id for a genuinely new attempt.
// Clears the cached result too so /plan/results doesn't render an
// old plan while the new call is in-flight. Fingerprint may be
// provided so subsequent idempotent retries continue to reuse this
// UUID for the same inputs.
export function startNewGenerationAttempt(fingerprint) {
  const id = makeUuid();
  writeMeta({ id, fingerprint: typeof fingerprint === 'string' ? fingerprint : '__forced__' });
  clearCachedGenerationResult();
  return id;
}

// Full reset — Start Over invokes this. No UUID or cached result
// survives.
export function clearGenerationRequest() {
  removeMeta();
  clearCachedGenerationResult();
}

// -------------------- Cached result ----------------------------

export function readCachedGenerationResult() {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeCachedGenerationResult(payload) {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

export function clearCachedGenerationResult() {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

// -------------------- RPC invocation ---------------------------

function logSanitized(label, err) {
  try {
    console.warn('[etff:generate]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch {
    /* ignore */
  }
}

// Invoke the RPC. Returns { ok, data, error, requestId } where
// `error` is a short customer-safe string on failure. Callers can
// use `requestId` (echoed from the request) to guard against a late
// response overwriting a fresher active generation.
export async function invokeGenerateMealPlans(generationRequestId) {
  if (!supabaseConfigured || !supabase) {
    return { ok: false, data: null, error: 'Sign-in is not available right now. Please try again later.', requestId: generationRequestId };
  }
  try {
    const { data, error } = await supabase.rpc('generate_meal_plans', {
      p_generation_request_id: generationRequestId,
    });
    if (error) {
      logSanitized('rpc:error', error);
      if (error.code === '42501' || String(error.message || '').toLowerCase().includes('permission')) {
        return { ok: false, data: null, error: 'Please sign in and try again.', requestId: generationRequestId };
      }
      return { ok: false, data: null, error: 'We could not build your plans. Please try again in a moment.', requestId: generationRequestId };
    }
    return { ok: true, data, error: null, requestId: generationRequestId };
  } catch (err) {
    logSanitized('rpc:threw', err);
    return { ok: false, data: null, error: 'We could not build your plans. Please try again in a moment.', requestId: generationRequestId };
  }
}
